import { useMemo } from 'react';
import flatten from 'lodash/flatten';
import intersection from 'lodash/intersection';

import { useEventRegistrationsQuery } from './useDashboardQueries';
import ParticipantFieldStats from './ParticipantFieldStats';
import ProductStats from './ProductStats';
import RegistrationsGraph from './RegistrationsGraph';
import TicketStatsContainer from './TicketStatsContainer';
import Totals from './Totals';
import UI from '../../common/UI';
import useDashboardUrlState from './useDashboardUrlState';
import useLocale from '../../common/useLocale';

export interface EventRegistrationsDashboardPageProps {
  eventId: string;
  startDate?: string;
}

const EventRegistrationsDashboardPage = ({
  eventId, startDate: defaultStartDate,
}: EventRegistrationsDashboardPageProps) => {
  const { formatDate, today } = useLocale();

  const [urlState] = useDashboardUrlState();

  /**
   * The start of today in the project's timezone converted to UTC
   */
  const startDate = defaultStartDate || formatDate(
    today(),
    { format: 'internal_date_time', timezone: null },
  );

  const { event, data } = useEventRegistrationsQuery(eventId, startDate);

  const { tickets, products, participantFields } = useMemo(() => {
    /**
     * The filtered tickets, or all tickets if there are no filters
     */
    const tickets = (urlState.tickets.length > 0
      ? event?.tickets.filter((ticket) => urlState.tickets.includes(ticket.id))
      : event?.tickets) || [];
    const ticketIds = tickets.map(({ id }) => id);

    /**
     * The products that belong to the filtered tickets, or all products if there are no filters
     */
    const products = event?.products.filter((product) => {
      const ticketIds = product.promotions
        .map((promotion) => promotion.restricted_to_tickets.map(({ id }) => id))
        .flat();

      return urlState.tickets.length === 0
        || product.sold_count > 0
        || ticketIds.length === 0
        || intersection(ticketIds, urlState.tickets).length > 0;
    }) || [];
    const productIds = products.map(({ id }) => id);

    const promotionIds = flatten([
      ...tickets.map((ticket) => ticket.promotions.map(({ id }) => id)),
      ...products.map((product) => product.promotions.map(({ id }) => id)),
    ]);

    /**
     * The participant fields that belong to the filtered tickets, or all participant fields if there are no filters
     */
    const participantFields = event?.participant_fields.filter((field) => (
      field.tickets.length + field.products.length + field.promotions.length === 0
        || intersection(field.tickets.map(({ id }) => id), ticketIds).length > 0
        || intersection(field.products.map(({ id }) => id), productIds).length > 0
        || intersection(field.promotions.map(({ id }) => id), promotionIds).length > 0
    )) || [];

    return {
      tickets,
      products,
      participantFields,
    };
  }, [event, urlState.tickets]);

  if (!event) {
    return <UI.PageLoader />;
  }

  return (
    <>
      <RegistrationsGraph eventId={eventId} data={data.registrationCounts} />
      <Totals event={event} />
      <TicketStatsContainer event={event} tickets={tickets} />
      {products.length > 0 && (
        <ProductStats products={products} />
      )}
      {participantFields.length > 0 && (
        <ParticipantFieldStats participantFields={participantFields} />
      )}
    </>
  );
};

export default EventRegistrationsDashboardPage;
